/**
 * One T1–T4 stage card in the protocol execution preview.
 */

import type { ProtocolFlowStage } from "./protocolFlowModel";

type ProtocolFlowStageCardProps = {
  stage: ProtocolFlowStage;
  selected: boolean;
  onSelectTier: (tier: string) => void;
  onOpenCriterion: (criterionId: string) => void;
};

/**
 * Render one execution-stage card with parallel criterion groups.
 *
 * @param props - Stage data and interaction callbacks
 * @returns Stage card element
 */
export function ProtocolFlowStageCard({
  stage,
  selected,
  onSelectTier,
  onOpenCriterion,
}: ProtocolFlowStageCardProps) {
  return (
    <section
      className={`flex min-w-[240px] max-w-[280px] flex-1 flex-col rounded-xl border p-3 ${
        selected
          ? "border-sky-700 bg-sky-50 shadow-sm"
          : "border-slate-200 bg-white"
      }`}
    >
      <button
        type="button"
        className="text-left"
        onClick={() => onSelectTier(stage.tier)}
        aria-pressed={selected}
      >
        <div className="text-sm font-semibold text-slate-900">
          {stage.label}
        </div>
        <p className="mt-1 text-xs text-slate-600">{stage.description}</p>
        <dl className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-slate-600">
          <div>
            <dt className="font-medium text-slate-500">Atoms</dt>
            <dd>{stage.atomCount}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Criteria</dt>
            <dd>{stage.criterionCount}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Hard stops</dt>
            <dd>{stage.hardStopCount}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Needs review</dt>
            <dd>{stage.needsReviewCount}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Gates</dt>
            <dd>{stage.gateCount}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Conditional</dt>
            <dd>{stage.conditionalCount}</dd>
          </div>
        </dl>
      </button>

      <div className="mt-3 flex-1 space-y-2 overflow-auto">
        {stage.criterionGroups.slice(0, 8).map((group) => (
          <button
            key={`${stage.tier}-${group.criterionId}`}
            type="button"
            className="w-full rounded border border-slate-200 bg-slate-50 px-2 py-2 text-left hover:border-slate-400"
            onClick={() => onOpenCriterion(group.criterionId)}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-slate-900">
                {group.criterionNo}
              </span>
              <span className="text-[10px] text-slate-500">
                {group.atomCount} atom{group.atomCount === 1 ? "" : "s"}
              </span>
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {group.hardStopCount > 0 ? (
                <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] text-red-700">
                  {group.hardStopCount} hard-stop
                </span>
              ) : null}
              {group.gateLabels.slice(0, 2).map((label) => (
                <span
                  key={label}
                  className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] text-indigo-700"
                >
                  {label}
                </span>
              ))}
              {group.spansMultipleTiers ? (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800">
                  multi-tier
                </span>
              ) : null}
            </div>
          </button>
        ))}
        {stage.criterionGroups.length > 8 ? (
          <p className="text-[11px] text-slate-500">
            +{stage.criterionGroups.length - 8} more criteria in this tier
          </p>
        ) : null}
        {stage.criterionGroups.length === 0 ? (
          <p className="rounded border border-dashed border-slate-200 px-2 py-3 text-center text-[11px] text-slate-500">
            No atoms in this tier
          </p>
        ) : null}
      </div>

      {stage.hardStopCount > 0 ? (
        <div className="mt-3 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] text-red-700">
          Hard-stop failure exits to Ineligible
        </div>
      ) : (
        <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] text-slate-600">
          Pass / unresolved continues to the next tier
        </div>
      )}
    </section>
  );
}
